import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ChatService } from '../../services/chat/chat.service';
import { LocalStorageService } from 'ngx-webstorage';

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  providers: [ChatService]
})
export class AiChatComponent implements OnInit {  
  isChatVisible = false;
  userQuestion: string = '';
  chatHistory: any[] = [];
  saveHistory: any[] =[];
  userRole: string = '';
  maxStorageSize: number = 4.5 * 1024 * 1024; 
  isTyping: boolean = false;
  userId: number | null = null;

  isDragging = false;
  dragStartX = 0;
  dragStartY = 0;
  buttonStartX = 0;
  buttonStartY = 0;
  currentX = 0;
  currentY = 0;

  @ViewChild('chatMessages') private chatMessagesContainer!: ElementRef;
  @ViewChild('chatToggle') private chatToggleButton!: ElementRef;

  constructor(
    private chatService: ChatService,
    private lsService: LocalStorageService) { 
    this.loadUserRole();
    this.loadChatHistory();
    this.loadButtonPosition();
  }

  ngOnInit() {
    if (this.chatHistory.length === 0) {
      this.chatHistory.push({
        user: 'AI',
        answer: 'Hello, how can I help you?',
        timestamp: new Date()
      });
      this.saveChatHistory();
    }
  }

    ngAfterViewInit() {
    // Set initial position from saved coordinates
    // this.updateButtonPosition();
    
    // Add event listeners for dragging
    this.addDragListeners();
  }

  addDragListeners() {
    const button = this.chatToggleButton.nativeElement;
    
    // Mouse events
    button.addEventListener('mousedown', this.onDragStart.bind(this));
    document.addEventListener('mousemove', this.onDragMove.bind(this));
    document.addEventListener('mouseup', this.onDragEnd.bind(this));
    
    // Touch events for mobile
    button.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.onDragEnd.bind(this));
  }

  onDragStart(e: MouseEvent) {
    e.preventDefault();
    this.isDragging = true;
    
    const rect = this.chatToggleButton.nativeElement.getBoundingClientRect();
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.buttonStartX = rect.left;
    this.buttonStartY = rect.top;
    
    this.chatToggleButton.nativeElement.style.transition = 'none';
  }

  onTouchStart(e: TouchEvent) {
    e.preventDefault();
    this.isDragging = true;
    
    const touch = e.touches[0];
    const rect = this.chatToggleButton.nativeElement.getBoundingClientRect();
    this.dragStartX = touch.clientX;
    this.dragStartY = touch.clientY;
    this.buttonStartX = rect.left;
    this.buttonStartY = rect.top;
    
    this.chatToggleButton.nativeElement.style.transition = 'none';
  }

  onDragMove(e: MouseEvent) {
    if (!this.isDragging) return;
    e.preventDefault();
    
    this.currentX = this.buttonStartX + (e.clientX - this.dragStartX);
    this.currentY = this.buttonStartY + (e.clientY - this.dragStartY);
    
    this.constrainToViewport();
    this.updateButtonPosition();
  }

  onTouchMove(e: TouchEvent) {
    if (!this.isDragging) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    this.currentX = this.buttonStartX + (touch.clientX - this.dragStartX);
    this.currentY = this.buttonStartY + (touch.clientY - this.dragStartY);
    
    this.constrainToViewport();
    this.updateButtonPosition();
  }

  onDragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.chatToggleButton.nativeElement.style.transition = 'all 0.3s ease';
    
    // Snap to edges
    this.snapToEdge();
    this.saveButtonPosition();
  }

  constrainToViewport() {
    const button = this.chatToggleButton.nativeElement;
    const buttonRect = button.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Constrain to viewport bounds
    this.currentX = Math.max(0, Math.min(this.currentX, viewportWidth - buttonRect.width));
    this.currentY = Math.max(0, Math.min(this.currentY, viewportHeight - buttonRect.height));
  }

  snapToEdge() {
    const viewportWidth = window.innerWidth;
    const buttonWidth = this.chatToggleButton.nativeElement.offsetWidth;
    
    // Snap to left or right edge based on which is closer
    if (this.currentX < viewportWidth / 2) {
      this.currentX = 20; // 20px from left edge
    } else {
      this.currentX = viewportWidth - buttonWidth - 20; // 20px from right edge
    }
    
    this.updateButtonPosition();
  }

  updateButtonPosition() {
    const button = this.chatToggleButton.nativeElement;
    button.style.position = 'fixed';
    button.style.left = `${this.currentX}px`;
    button.style.top = `${this.currentY}px`;
    button.style.right = 'auto';
    button.style.bottom = 'auto';
  }

  saveButtonPosition() {
    this.lsService.store('chatButtonPosition', {
      x: this.currentX,
      y: this.currentY
    });
  }

  loadButtonPosition() {
    const savedPosition = this.lsService.retrieve('chatButtonPosition');
    if (savedPosition) {
      this.currentX = savedPosition.x;
      this.currentY = savedPosition.y;
    } else {
      // Default position (bottom right)
      this.currentX = window.innerWidth - 80;
      this.currentY = window.innerHeight - 80;
    }
  }

  toggleChat() {
    if (!this.isDragging) {
      this.isChatVisible = !this.isChatVisible;
      setTimeout(() => this.scrollToBottom(), 0);
    }
  }
  
  loadUserRole() {
    const userDetails = this.lsService.retrieve('web_user_details');
    if (userDetails) {
      const userRole = userDetails.user_role;
      this.userId = userDetails?userDetails.id: null;
      
      switch(userRole) {
        case 1:
          this.userRole = 'super_admin';
          break;
        case 2:
          this.userRole = 'user';
          break;
        case 3:
          this.userRole = 'admin';
          break;
      }
    }
  }

  sendMessage() {
    if (this.userQuestion.trim()) {
      this.chatHistory.push({
        user: 'User',
        question: this.userQuestion, 
        timestamp: new Date()
      });
      this.isTyping = true;
      setTimeout(()=> this.scrollToBottom(),0);
      this.saveHistory = this.lsService.retrieve('chatHistory') || [];
      if (typeof this.saveHistory === 'string') {
        this.saveHistory = JSON.parse(this.saveHistory);
      }

      if (this.saveHistory.length > 0 && this.saveHistory[0].answer === 'Hello, how can I help you?') {
        this.saveHistory.shift();
      }
      
      const formattedChatHistory = [];

      for (let i = 0; i < this.saveHistory.length; i += 2) {
        if (this.saveHistory[i]&&this.saveHistory[i].user === 'User' && this.saveHistory[i + 1]&&this.saveHistory[i + 1].user === 'AI') {
          formattedChatHistory.push({
            User: this.saveHistory[i].question.replace(/\n/g, '').trim(),
            AI: this.saveHistory[i + 1].answer.replace(/\n/g, '').trim()
          });
        }
      }
      formattedChatHistory.push({
        User: this.userQuestion.trim()
      });

      this.chatService.getChatResponse(this.userRole, this.userQuestion, this.userId, formattedChatHistory.slice(-10)).subscribe(
        (response) => {
          this.isTyping = false;
          if (response.response_answer.includes('##NOANSWER')) {
            // Display the predefined questions when no data is available
            const noDataMessage = `
            Sorry, I couldn't find the data.<br>
            Please provide the name of the platform (Marketplaces/Social Media/Standalone),
            the date range (From date and To date),
            and the brand name for which you are seeking data in the question.
            <br>For example, 
            <br>What is the most popular product name in social media for the brand (brand_name) from January 2024 to the current date?
          `;
            
            // Add AI's response and the predefined questions to the chat history
            this.chatHistory.push({
              user: 'AI',
              answer: noDataMessage,
              timestamp: new Date()
            });

          } else {
            this.chatHistory.push({ 
              user: 'AI', 
              answer: response.response_answer, 
              timestamp: new Date()
            });
          }
            this.saveChatHistory();
            this.scrollToBottom();
        },
        (error) => {
          this.isTyping = false;
          console.error('Error getting chat response:', error);
          this.chatHistory.push({ 
            user: 'AI', 
            answer: 'Sorry, I encountered an error. Please try again later.', 
            timestamp: new Date()
          });
          this.saveChatHistory();
          this.scrollToBottom();
        }
      );

      this.userQuestion = '';
      this.isTyping = true;
    }
  }

  loadChatHistory() {
    const savedHistory = this.lsService.retrieve('chatHistory');
    this.chatHistory = savedHistory ? JSON.parse(savedHistory) : [];
  }

  saveChatHistory() {
    
    while (this.getStorageSize() > this.maxStorageSize && this.chatHistory.length > 1) {
      this.chatHistory.shift();
    }
    this.lsService.store('chatHistory', JSON.stringify(this.chatHistory));
  }

  getStorageSize(): number {
    let totalSize = 0;
    for (let key in this.lsService) {
      if (this.lsService.hasOwnProperty(key)) {
        totalSize += this.lsService[key].length * 2;
      }
    }
    return totalSize;
  }

  scrollToBottom() {
    try {
      setTimeout(() => {
        this.chatMessagesContainer.nativeElement.scrollTop = this.chatMessagesContainer.nativeElement.scrollHeight;
      }, 0);
    } catch (err) {
      console.error('Error while scrolling to the bottom:', err);
    }
  }
  openHelpPage() {
    window.open('assets/help.html', '_blank', 'width=800,height=600,left=200,top=100');
  }
}